import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  ApolloProvider as GQLProvider,
  NormalizedCacheObject,
  useQuery,
} from '@apollo/client'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'
import apiEndpoint from 'config/apiEndpoint'

import * as fragments from './fragments'
import * as queries from './queries'

import { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { GenericError, useAuth0, withAuthenticationRequired } from '@auth0/auth0-react'
import { setAuthData } from '~/store/auth'
import { useAppDispatch } from '~/store/hooks'
import { Modal } from '~/ui/Modal'
import { Dialog } from '~/ui/Dialog'
import { Heading } from 'react-aria-components'
import { Button } from '~/ui/Button'
import { captureException } from '@sentry/react'
import { Loader } from '~/ui/Loader'

const customFetch = ((_, options) => {
  const event = new CustomEvent('online')
  dispatchEvent(event)

  if (navigator.onLine) {
    return fetch(apiEndpoint, options)
      .then(res => {
        const apiEvent = new CustomEvent('apiStatus', {
          detail: { status: true },
        })
        dispatchEvent(apiEvent)

        return res
      })
      .catch(err => {
        const apiEvent = new CustomEvent('apiStatus', {
          detail: { status: false },
        })
        const isAbortedRequest = err.name === 'AbortError'
        if (!isAbortedRequest) dispatchEvent(apiEvent)

        throw err
      })
  }
}) as typeof fetch

const httpLink = new BatchHttpLink({ fetch: customFetch })

function checkNetworkStatus() {
  const URL = '/status'
  const xhr = new XMLHttpRequest()
  return new Promise((resolve, reject) => {
    xhr.onload = () => {
      resolve(true)
    }
    xhr.onerror = () => {
      reject(false)
    }
    xhr.open('GET', URL, true)
    xhr.send()
  })
}

const link = ApolloLink.from([httpLink])

const cache = new InMemoryCache()

const client: ApolloClient<NormalizedCacheObject> =
  'process' in globalThis && 'VITEST_WORKER_ID' in process.env
    ? (new Proxy(
        {},
        {
          get() {
            throw new Error('using apollo client directly in test')
          },
        }
      ) as unknown as ApolloClient<NormalizedCacheObject>)
    : new ApolloClient({
        link,
        cache,
      })

// TODO: The previous implementation of auth would set auth data into the redux store. This is
// unnecessary now because Auth0 manages that in the provider. This component keeps compatibility
// with the previous implementation. Remove it after all other components have been updated to use
// the `useAuth0` hook.
const AuthDataReduxSideEffect = () => {
  const { user } = useAuth0()
  const dispatch = useAppDispatch()
  const { data } = useQuery(queries.GET_USER_QUERY, { variables: { auth0UserId: user?.sub }, skip: !user })
  useEffect(() => {
    if (!user || !data?.User) return
    dispatch(
      setAuthData({
        name: user.name ?? user.email,
        email: user.email,
        avatar: user.picture,
        userId: data.User.id,
      })
    )
  }, [user, data, dispatch])
  return <></>
}

const UserSessionExpiredModal = ({ isOpen }: { isOpen: boolean }) => {
  const { loginWithRedirect } = useAuth0()

  const handleTriggerAuthentication = () => {
    loginWithRedirect({
      appState: {
        returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      },
    })
  }

  return (
    <Modal isOpen={isOpen} isDismissable={false}>
      <Dialog>
        <Heading slot="title" className="text-lg font-bold pb-3">
          Session Expired
        </Heading>
        <div className="flex flex-col justify-center gap-3">
          <p>Please log back in to continue.</p>
          <Button onClick={handleTriggerAuthentication}>Log In</Button>
        </div>
      </Dialog>
    </Modal>
  )
}

type TokenResult =
  | { status: 'success'; accessToken: string }
  | { status: 'error'; error: Error }
  | { status: 'loading'; accessToken?: never; error?: never }

const useAccessToken = () => {
  const { getAccessTokenSilently, isLoading } = useAuth0()
  const [result, setResult] = useState<TokenResult>({ status: 'loading' })

  useEffect(() => {
    if (isLoading) return
    ;(async () => {
      try {
        const accessToken = await getAccessTokenSilently()
        setResult({ status: 'success', accessToken })
      } catch (error) {
        if (error instanceof GenericError && error.error === 'login_required') return
        else if (error instanceof Error) setResult({ status: 'error', error })
        console.error(error)
        captureException(error)
      }
    })()
  }, [getAccessTokenSilently, isLoading])

  return result
}

const ApolloProvider = withAuthenticationRequired(({ children }: PropsWithChildren) => {
  const tokenResult = useAccessToken()
  const [isSessionExpired, setIsSessionExpired] = useState(false)

  const apolloClient = useMemo(() => {
    if (!(tokenResult.status === 'success')) return

    const authLink = setContext(context => ({
      ...context,
      headers: {
        ...('headers' in context && typeof context.headers === 'object' ? context.headers : {}),
        authorization: `Bearer ${tokenResult.accessToken}`,
      },
    }))

    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) console.error(graphQLErrors)
      if (networkError) {
        const isUnauthorizedServerResponse = 'statusCode' in networkError && networkError.statusCode === 401
        // TODO: Backend does not send correct error codes in error handling middleware
        // Remove this after updates to the backend
        const _deprecatedIsUnauthorizedServerResponse =
          'result' in networkError &&
          typeof networkError.result === 'object' &&
          networkError.result.name === 'InvalidTokenError'
        if (isUnauthorizedServerResponse || _deprecatedIsUnauthorizedServerResponse) {
          setIsSessionExpired(true)
        } else if (networkError.message === 'Failed to fetch') {
          checkNetworkStatus()
            .then(() => {
              const event = new CustomEvent('online')
              const apiEvent = new CustomEvent('apiStatus', {
                detail: { status: false },
              })
              dispatchEvent(event)
              dispatchEvent(apiEvent)
            })
            .catch(() => {
              const event = new CustomEvent('forceOffline')
              dispatchEvent(event)
            })
        }
      }
    })
    const link = ApolloLink.from([errorLink, authLink, httpLink])
    // TODO: Replace global instance with useApolloClient and just return new ApolloClient here
    client.setLink(link)
    return client
  }, [tokenResult])

  return (
    <>
      <UserSessionExpiredModal isOpen={isSessionExpired || tokenResult.status === 'error'} />
      {apolloClient ? (
        <GQLProvider client={client}>
          <AuthDataReduxSideEffect />
          {children}
        </GQLProvider>
      ) : (
        <Loader variant="centered" />
      )}
    </>
  )
})

export { client, queries, fragments, ApolloProvider, cache }
export default client
